import styled, { css, keyframes } from 'styled-components';

/* ==========================================================================
   TableEmptyState — animowany pusty stan dla tabel / kart.
   Ilustracje to samodzielne, animowane SVG (bez zewnętrznego GIF-a), w stonowanej
   line-artowej stylistyce. Animacje gasną przy `prefers-reduced-motion`.
   ========================================================================== */

// Uwaga: wejście animujemy wyłącznie przez `opacity`. Wcześniejsze
// `translateY(8px)` powodowało, że przez czas animacji treść wystawała poza dół
// kontenera tabeli (`ListTableWrap` ma `overflow-x: auto`, co wg specyfikacji CSS
// promuje też oś Y do `auto`) — na moment pojawiał się pionowy scroll.
const containerFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// `data-table-empty-state` — marker dla globalnej reguły w index.css, która
// wyłącza hover na wierszu tabeli zawierającym pusty stan (komunikat nie ma się
// podświetlać na szaro po najechaniu).
export const EmptyContainer = styled.div.attrs<{ 'data-table-empty-state'?: string }>({
  'data-table-empty-state': '',
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 44px 24px;
  gap: 10px;
  text-align: center;
  animation: ${containerFadeIn} 0.45s ease-out;

  @media (max-width: 768px) {
    padding: 36px 20px;
  }
`;

export const IllustrationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  /* Stonowany kolor line-artu — taki sam jak inne puste stany w panelu. */
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};

  svg {
    width: auto;
    height: 132px;
  }

  @media (max-width: 768px) {
    svg {
      height: 108px;
    }
  }
`;

// Samodzielny wrapper na animowaną ilustrację (bez tytułu/opisu) — np. w
// nagłówkach kreatorów (SetupWizard) zamiast statycznej ikony.
export const EmptyStateArtWrap = styled.div<{ $height: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};

  svg { width: auto; height: ${({ $height }) => $height}px; }
`;

export const EmptyTitle = styled.p`
  margin: 0;
  font-size: 15px;
  /* Nagłówek nosi całą informację („nie udało się pobrać danych") — przy wadze
     300 ginął w tle ilustracji i wyglądał jak podpis, a nie komunikat. */
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const EmptyDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 380px;
`;

export const EmptyActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
`;

/* ==========================================================================
   Klatki animacji ilustracji
   ========================================================================== */

// Delikatne kołysanie dzwonka (efekt „dzwonienia") + drobne podskoki.
const bellRing = keyframes`
  0%, 62%, 100% { transform: rotate(0deg); }
  68% { transform: rotate(7deg); }
  74% { transform: rotate(-6deg); }
  80% { transform: rotate(4deg); }
  86% { transform: rotate(-3deg); }
  92% { transform: rotate(1deg); }
`;

// Fale dźwięku rozchodzące się na boki, zsynchronizowane z dzwonieniem.
const soundPulse = keyframes`
  0%, 60% { opacity: 0; transform: scale(0.6); }
  70% { opacity: 0.9; }
  100% { opacity: 0; transform: scale(1.25); }
`;

// Powolne unoszenie całego motywu (kalendarz, dymki, taca).
const floaty = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

// Migotanie ozdobnych znaczków (+ / x / romb).
const twinkle = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(0.85); }
  50% { opacity: 0.7; transform: scale(1.05); }
`;

// Pulsowanie wyróżnionego elementu (dzień w kalendarzu, „ptaszek").
const popPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50% { transform: scale(1.12); opacity: 1; }
`;

// Skaczące kropki „pisania" w dymku ankiety.
const typingBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.45; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

// Powolny dryf tła (miękkie obłoki za motywem).
const blobDrift = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(6px); }
`;

// „Oddychające" słupki wykresu — efekt żywych danych.
const barGrow = keyframes`
  0%, 100% { transform: scaleY(0.7); }
  50% { transform: scaleY(1); }
`;

// Rozchodzący się „ping" aktywności (pierścień rosnący i gasnący).
const ping = keyframes`
  0% { opacity: 0.55; transform: scale(0.4); }
  80%, 100% { opacity: 0; transform: scale(1.7); }
`;

// Obrót wskazówki zegara (rezerwacje wypożyczalni).
const sweep = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Delikatne „skanowanie" lupy w poziomie (puste wyniki wyszukiwania).
const scan = keyframes`
  0%, 100% { transform: translateX(-3px); }
  50% { transform: translateX(3px); }
`;

// Linia skanująca kod QR przesuwająca się w dół i z powrotem (generator QR).
const qrScan = keyframes`
  0%, 100% { transform: translateY(-16px); opacity: 0.3; }
  50% { transform: translateY(16px); opacity: 0.9; }
`;

// Fale dźwięku megafonu rozchodzące się w bok (alerty / ogłoszenia).
const waveOut = keyframes`
  0% { opacity: 0; transform: translateX(-4px) scale(0.85); }
  45% { opacity: 0.7; }
  100% { opacity: 0; transform: translateX(5px) scale(1.05); }
`;

// Mrugnięcie oczu robota (AI agent) — krótkie domknięcie powiek.
const blink = keyframes`
  0%, 88%, 100% { transform: scaleY(1); }
  93% { transform: scaleY(0.12); }
`;

// Pulsująca dioda na antenie robota.
const beacon = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.35); opacity: 0.5; }
`;

// Migocząca iskra w przerwie rozłączonego kabla (stan błędu).
const flash = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(0.92); }
  20% { opacity: 1; transform: scale(1.08); }
  35% { opacity: 0.45; }
  55% { opacity: 1; transform: scale(1.04); }
  75% { opacity: 0.4; }
`;

// Kołysząca się igła kompasu — „szukanie zagubionego kierunku" (404).
const wobble = keyframes`
  0%, 100% { transform: rotate(-24deg); }
  50% { transform: rotate(24deg); }
`;

// Unoszący się „w górę" badge uploadu (strzałka) — subtelny ruch + zanik.
const arrowLift = keyframes`
  0%, 100% { transform: translateY(0); opacity: 1; }
  50% { transform: translateY(-4px); opacity: 0.85; }
`;

const reduceMotion = css`
  @media (prefers-reduced-motion: reduce) {
    * { animation: none !important; }
  }
`;

// Wspólna baza dla animowanych elementów SVG — zapewnia poprawny punkt obrotu.
const animatedSvg = css`
  display: block;

  [class^='blob'] {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${blobDrift} 9s ease-in-out infinite;
  }
  .blob-2 { animation-duration: 11s; animation-direction: reverse; }

  .spark {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${twinkle} 3.2s ease-in-out infinite;
  }
  .spark-2 { animation-delay: 0.8s; }
  .spark-3 { animation-delay: 1.6s; }

  ${reduceMotion}
`;

export const OrdersSvg = styled.svg`
  ${animatedSvg};

  .bell {
    transform-box: fill-box;
    transform-origin: bottom center;
    animation: ${bellRing} 3.6s ease-in-out infinite;
  }
  .wave {
    transform-box: fill-box;
    transform-origin: bottom center;
    animation: ${soundPulse} 3.6s ease-in-out infinite;
  }
  .wave-r { animation-delay: 0.12s; }
`;

export const EventsSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.5s ease-in-out infinite;
  }
  .day {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 2.8s ease-in-out infinite;
  }
`;

export const SurveysSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.8s ease-in-out infinite;
  }
  .dot {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${typingBounce} 1.4s ease-in-out infinite;
  }
  .dot-2 { animation-delay: 0.2s; }
  .dot-3 { animation-delay: 0.4s; }
`;

export const GenericSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.5s ease-in-out infinite;
  }
`;

export const QrSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
  /* Pozioma linia „skanująca" przesuwająca się w dół po kodzie QR. */
  .qr-scan {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${qrScan} 2.6s ease-in-out infinite;
  }
  /* Pulsujący, akcentowy moduł QR (róg pozycjonujący). */
  .qr-pulse {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 2.6s ease-in-out infinite;
  }
`;

export const AnalyticsSvg = styled.svg`
  ${animatedSvg};

  .bar {
    transform-box: fill-box;
    transform-origin: bottom center;
    animation: ${barGrow} 2.8s ease-in-out infinite;
  }
  .bar-2 { animation-delay: 0.3s; }
  .bar-3 { animation-delay: 0.6s; }
  .bar-4 { animation-delay: 0.9s; }

  .peak {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 2.4s ease-in-out infinite;
  }
  .trend {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
`;

export const ActivitySvg = styled.svg`
  ${animatedSvg};

  .peak {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 2.2s ease-in-out infinite;
  }
  .ping {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${ping} 2.4s ease-out infinite;
  }
  .feed {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 5s ease-in-out infinite;
  }
`;

export const ShopSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.4s ease-in-out infinite;
  }
  .tag {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 3.6s ease-in-out infinite;
    animation-delay: 0.5s;
  }
`;

export const RentalsSvg = styled.svg`
  ${animatedSvg};

  .hand {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${sweep} 9s linear infinite;
  }
  .tag {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 3.8s ease-in-out infinite;
  }
`;

export const SearchSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
  .scan {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${scan} 3.2s ease-in-out infinite;
  }
`;

export const OffersSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.4s ease-in-out infinite;
  }
  .pop {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 2.6s ease-in-out infinite;
  }
`;

export const ReportsSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.5s ease-in-out infinite;
  }
  .pop {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 2.4s ease-in-out infinite;
  }
`;

export const PoiSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4s ease-in-out infinite;
  }
  .ping {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${ping} 2.6s ease-out infinite;
  }
`;

export const GallerySvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.4s ease-in-out infinite;
  }
  .sun {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 3s ease-in-out infinite;
  }
`;

// Upload — dokument + miniatura obrazka („grafiki i dokumenty") z pulsującym
// akcentem i unoszącym się badge'em strzałki w górę.
export const UploadSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
  .sun {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 3s ease-in-out infinite;
  }
  .arrow {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${arrowLift} 2.6s ease-in-out infinite;
  }
`;

export const PersonSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.2s ease-in-out infinite;
  }
  .pop {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 2.4s ease-in-out infinite;
  }
`;

export const GroupSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.4s ease-in-out infinite;
  }
  .lead {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${popPulse} 2.8s ease-in-out infinite;
  }
`;

// Organizacje — klaster budynków z akcentowanym HQ (maszt + flaga), który delikatnie
// „oddycha". Używane dla pustego stanu / błędu ładowania listy organizacji.
export const OrganizationsSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
  .lead {
    transform-box: fill-box;
    transform-origin: center bottom;
    animation: ${popPulse} 3s ease-in-out infinite;
  }
`;

export const SpaSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
  .bloom {
    transform-box: fill-box;
    transform-origin: bottom center;
    animation: ${popPulse} 3s ease-in-out infinite;
  }
  .ripple {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${ping} 3.4s ease-out infinite;
  }
`;

export const SetupSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.4s ease-in-out infinite;
  }
  .gear {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${sweep} 7s linear infinite;
  }
`;

export const AlertsSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
  .wave {
    transform-box: fill-box;
    transform-origin: left center;
    animation: ${waveOut} 2.4s ease-out infinite;
  }
  .wave-2 { animation-delay: 0.4s; }
`;

export const AiSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
  .blink {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${blink} 4s ease-in-out infinite;
  }
  .beacon {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${beacon} 2.2s ease-in-out infinite;
  }
  .ripple {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${ping} 3.4s ease-out infinite;
  }
  .dot {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${typingBounce} 1.4s ease-in-out infinite;
  }
  .dot-2 { animation-delay: 0.2s; }
  .dot-3 { animation-delay: 0.4s; }
`;

// Stan błędu — rozłączony kabel z migoczącą iskrą w przerwie.
export const ErrorSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 4.6s ease-in-out infinite;
  }
  .zap {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${flash} 2s ease-in-out infinite;
  }
`;

// 404 — kompas z kołyszącą się igłą („zagubiony kierunek").
export const NotFoundSvg = styled.svg`
  ${animatedSvg};

  .floaty {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${floaty} 5s ease-in-out infinite;
  }
  .needle {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${wobble} 4.2s ease-in-out infinite;
  }
`;
